import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { SignInPanel } from "@/components/account/SignInPanel";
import { AccountNav } from "@/components/account/AccountNav";
import { AccountOverview } from "@/components/account/AccountOverview";
import { OrderCard } from "@/components/account/OrderCard";
import { EmptyOrders } from "@/components/account/EmptyOrders";
import { ProfileCard } from "@/components/account/ProfileCard";

const TABS = ["overview", "orders", "profile"];

export default function AccountPage() {
  const { customer, isLoggedIn, logout, refresh, loading } = useAuth();
  const [accountUrl, setAccountUrl] = useState("");
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const setTab = (t) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });

  useEffect(() => {
    api.authConfig().then((c) => setAccountUrl(c.account_url || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (isLoggedIn) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  if (loading) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-24" data-testid="account-loading">
        <div className="h-64 animate-pulse rounded-2xl bg-neutral-200/70" />
      </main>
    );
  }

  if (!isLoggedIn) return <SignInPanel accountUrl={accountUrl} />;

  const orders = customer.orders || [];

  return (
    <main data-testid="account-page" className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="grid gap-8 lg:grid-cols-[260px_1fr] lg:gap-12">
        <Reveal>
          <AccountNav
            active={tab}
            onChange={setTab}
            customer={customer}
            onLogout={async () => {
              toast.success("Logged out");
              await logout();
            }}
          />
        </Reveal>

        <Reveal delay={0.08} className="min-w-0">
          {tab === "overview" && <AccountOverview customer={customer} onViewOrders={() => setTab("orders")} />}

          {tab === "orders" && (
            <div data-testid="account-orders-tab">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">My Account</p>
              <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">Order history</h1>
              <p className="mt-1 text-sm text-neutral-500">
                {orders.length ? `${orders.length} order${orders.length === 1 ? "" : "s"} placed with ${customer.email}` : "Nothing here yet."}
              </p>
              <div className="mt-6 space-y-4" data-testid="account-orders">
                {orders.length ? orders.map((o) => <OrderCard key={o.id} order={o} />) : <EmptyOrders />}
              </div>
            </div>
          )}

          {tab === "profile" && (
            <div data-testid="account-profile-tab">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">My Account</p>
              <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">Your details</h1>
              <div className="mt-6">
                <ProfileCard customer={customer} accountUrl={accountUrl} />
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </main>
  );
}
